import { API_BASE } from '../config/apiBase';
import { useState, useCallback, useRef } from 'react';
import axios from 'axios';

const API = API_BASE;

/**
 * Custom hook for Text-to-Speech using ElevenLabs API
 * Provides high-quality AI-generated voice narration
 * Audio is cached on the server - ElevenLabs is only called once per lesson!
 */
export function useElevenLabsTTS() {
  const [speaking, setSpeaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);

  /**
   * Generate speech from text using ElevenLabs (with server-side caching)
   * @param {string} text - Text to convert to speech
   * @param {object} options - Options including lang and lessonId
   */
  const speak = useCallback(async (text, options = {}) => {
    if (!text || text.trim().length === 0) {
      setError('No text provided');
      return;
    }

    // Stop any current playback
    stop();
    
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API}/training/tts/generate`,
        { 
          text: text.substring(0, 5000), // ElevenLabs has text limits
          language: options.lang || 'es',
          lesson_id: options.lessonId || null  // NEW: Pass lesson ID for efficient caching
        },
        { 
          responseType: 'blob',
          timeout: 120000 // 2 minute timeout for long texts (may need to generate)
        }
      );

      // Check if we got a cache hit (no credits used!)
      const cacheStatus = response.headers['x-cache'];
      if (cacheStatus === 'HIT') {
        console.log('✅ TTS served from cache - no ElevenLabs credits used!');
      } else {
        console.log('⚡ TTS generated - now cached for future use');
      }

      // Create audio URL from blob
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
      
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      audioUrlRef.current = URL.createObjectURL(audioBlob);

      // Create and play audio
      const audio = new Audio(audioUrlRef.current);
      audioRef.current = audio;

      audio.onplay = () => {
        setSpeaking(true);
        setLoading(false);
      };

      audio.onended = () => {
        setSpeaking(false);
      };

      audio.onerror = (e) => {
        console.error('Audio playback error:', e);
        setError('Error playing audio');
        setSpeaking(false);
        setLoading(false);
      };

      await audio.play();
    } catch (err) {
      console.error('ElevenLabs TTS error:', err);
      setError(err.response?.data?.detail || 'Error generating speech');
      setLoading(false);
      setSpeaking(false);
    }
  }, []);

  /**
   * Stop current playback
   */
  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setSpeaking(false);
    setLoading(false);
  }, []);

  /**
   * Pause current playback
   */
  const pause = useCallback(() => {
    if (audioRef.current && speaking) {
      audioRef.current.pause();
    }
  }, [speaking]);

  /**
   * Resume paused playback
   */
  const resume = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.play();
    }
  }, []);

  /**
   * Toggle play/pause
   */
  const toggle = useCallback(() => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play();
      } else {
        audioRef.current.pause();
      }
    }
  }, []);

  /**
   * Check if audio is paused
   */
  const isPaused = audioRef.current?.paused && speaking;

  return {
    speak,
    stop,
    pause,
    resume,
    toggle,
    speaking,
    loading,
    error,
    paused: isPaused,
    supported: true // Always supported since it's server-side
  };
}

export default useElevenLabsTTS;
